/**
 * 팝업 창(모달) 공용 틀 — 오버레이 · 창 · 제목줄(아이콘+제목 · 오른쪽 조작 · 닫기) · Esc · 포커스.
 *
 * 모양은 styles/appModal.css 의 공통 클래스가 맡고, 이 컴포넌트는 **구조와 동작**을 한 곳에 묶는다.
 * 새 창은 이것으로 만든다 — 창마다 overlay/header/close 를 다시 그리면 모양과 동작이 창마다
 * 벌어진다(모니터링 창 11벌이 그랬다). 규칙·옮길 창 목록: docs/ui-modal-consistency.md
 *
 * 동작
 *  - Esc: **창 안에 포커스가 있을 때만** 닫는다. window 에 걸면 이 창 위에 뜬 확인 대화상자의 Esc 까지
 *    받아 창이 통째로 닫힌다. 안쪽 창이 먼저 받고 전파를 멈추므로 창 안에 창이 있어도 한 겹만 닫힌다.
 *  - 포커스: 열리면 창으로 옮기고(안쪽에 이미 포커스가 있으면 그대로), 닫히면 원래 자리로 돌려준다.
 *    그래야 열자마자 Esc 가 먹고, 닫은 뒤 키보드 사용자가 제자리에서 이어 간다.
 *  - 바깥 클릭: 기본은 닫지 않는다(closeOnBackdrop). 입력하던 것을 잃기 쉽다.
 *  - 창 크기·전체화면·본문 배치는 창마다 다르므로 className 들로 넘긴다. className 을 주지 않으면
 *    기본 크기(app-modal-default)를 쓴다.
 *  - windowed(2026-10-06): 제목줄을 끌어 이동 · ⤢ 앱 안 최대화(제목줄 더블클릭도) · ⧉ 새 OS 창으로 분리(다른 모니터로 옮겨
 *    그 창에서 전체 화면). 모니터링·오라클 전문가 창들이 켠다 — 확인·입력 같은 작은 대화상자에는 켜지 않는다. 구현: useModalWindow
 *    작업 탭(2026-10-19): windowed 창은 팝업이 아니라 편집기 자리의 탭으로 열린다(components/workspace/WorkspaceDock). 탭에서는
 *    이동·최대화가 없고 Esc 로 닫지 않는다 — 탭의 ✕ 나 창의 닫기로 닫는다. 「새 창으로 분리」 는 그대로다.
 */
import React, { useEffect, useId } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { FiX } from 'react-icons/fi';
import useModalWindow from './useModalWindow';

const cx = (...parts) => parts.filter(Boolean).join(' ');

function AppModal({
    open,
    onClose,
    title,
    icon,
    titleExtra,
    headerExtra,
    children,
    className,
    overlayClassName,
    headerClassName,
    closeClassName,
    closeLabel,
    closeOnEsc = true,
    closeOnBackdrop = false,
    showClose = true,
    windowed = false,
    withResult = false,
}) {
    const { t } = useTranslation();
    const win = useModalWindow({ title: typeof title === 'string' ? title : undefined, enabled: windowed, onClose, icon, withResult });
    const { frameRef } = win;
    const titleId = useId();
    // 닫으면 다음엔 제자리·앱 안에서 시작한다(분리한 창도 닫힌다)
    const { reset } = win;
    useEffect(() => { if (!open) reset(); }, [open, reset]);

    // 훅은 아래 early return 보다 앞에 둔다 — 닫힌 렌더와 열린 렌더의 훅 순서가 같아야 한다
    useEffect(() => {
        if (!open) return undefined;
        const previous = document.activeElement;
        const frame = frameRef.current;
        if (frame && !frame.contains(document.activeElement)) {
            frame.focus({ preventScroll: true });
        }
        return () => {
            if (previous && typeof previous.focus === 'function' && document.contains(previous)) {
                previous.focus({ preventScroll: true });
            }
        };
    }, [open, frameRef]);   // frameRef 는 훅이 주는 고정 ref

    if (!open) return null;

    const handleKeyDown = (e) => {
        // 작업 탭에 열린 화면은 Esc 로 닫지 않는다 — 편집 중 Esc(자동완성 닫기 등)에 화면이 통째로 사라진다
        if (e.key !== 'Escape' || !closeOnEsc || e.defaultPrevented || win.docked) return;
        e.stopPropagation();
        if (onClose) onClose();
    };

    // 누른 곳이 오버레이 자체일 때만 — 창 안에서 끌다가 바깥에서 놓은 것까지 닫히면 안 된다
    const handleBackdrop = (e) => {
        if (closeOnBackdrop && e.target === e.currentTarget && onClose) onClose();
    };

    const label = closeLabel || t('common:ui.close');

    return win.wrap(
        <div className={cx('app-modal-overlay', overlayClassName)} onMouseDown={handleBackdrop} role="presentation" style={win.overlayStyle}>
            <div
                ref={frameRef}
                className={cx('app-modal', className || 'app-modal-default')}
                style={win.frameStyle}
                data-app-modal
                role="dialog"
                aria-modal="true"
                aria-labelledby={titleId}
                tabIndex={-1}
                onKeyDown={handleKeyDown}
            >
                <div className={cx('app-modal-header', headerClassName)} {...win.headerProps}>
                    <h2 id={titleId} className="app-modal-title">{icon}{title}</h2>
                    {titleExtra && <div className="app-modal-title-extra">{titleExtra}</div>}
                    {(headerExtra || showClose || windowed) && (
                        <div className="app-modal-header-right">
                            {headerExtra}
                            {win.controls}
                            {showClose && (
                                <button
                                    type="button"
                                    className={cx('app-modal-close', closeClassName)}
                                    onClick={onClose}
                                    aria-label={label}
                                    title={label}
                                >
                                    <FiX size={16} />
                                </button>
                            )}
                        </div>
                    )}
                </div>
                {children}
            </div>
        </div>
    );
}

AppModal.propTypes = {
    /** false 면 아무것도 그리지 않는다 */
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func,
    /** 제목 — 메뉴와 같은 문구 키로 번역한 값 */
    title: PropTypes.node.isRequired,
    /** 메뉴와 같은 아이콘, 14px (예: <FiUserX size={14} />) */
    icon: PropTypes.node,
    /** 제목 바로 오른쪽 — 남은 폭을 차지한다(오라클 전문가 창의 DB 연결 탭, 2026-10-22). 끌기는 단추·선택 상자에서 시작하지 않는다 */
    titleExtra: PropTypes.node,
    /** 제목줄 오른쪽, 닫기 앞 — 연결 선택·새로고침 등. 아이콘 버튼은 app-modal-iconbtn */
    headerExtra: PropTypes.node,
    children: PropTypes.node,
    /** 창 크기·배치를 정하는 창 고유 클래스. 없으면 기본 크기 */
    className: PropTypes.string,
    overlayClassName: PropTypes.string,
    headerClassName: PropTypes.string,
    closeClassName: PropTypes.string,
    /** 닫기 버튼의 접근성 이름. 기본 common:ui.close */
    closeLabel: PropTypes.string,
    closeOnEsc: PropTypes.bool,
    closeOnBackdrop: PropTypes.bool,
    showClose: PropTypes.bool,
    /** 이동 · 최대화 · 새 창으로 분리(다른 모니터에서 전체 화면) — 모니터링·오라클 전문가 창 */
    windowed: PropTypes.bool,
    /** 작업 탭에서 아래에 공용 결과 영역(SQL 편집의 결과 탭)을 함께 보인다 — SQL 을 실행하는 화면(SQL 작성) */
    withResult: PropTypes.bool,
};

export default AppModal;
